/**
 * Access to the desktop shell, safe to call from anywhere.
 *
 * Every consumer goes through here rather than touching `window.cyphexDesktop`,
 * so the "we are in a browser" branch is written once and there is exactly one
 * place to look when the bridge changes shape.
 */
import type { DesktopBridge } from './types';

export function desktopBridge(): DesktopBridge | null {
  if (typeof window === 'undefined') return null;
  const bridge = window.cyphexDesktop;
  return bridge !== undefined && bridge.isDesktop === true ? bridge : null;
}

export function isDesktopShell(): boolean {
  return desktopBridge() !== null;
}

/**
 * The desktop client can share computer sound with THIS CALL taken back out of
 * it (desktop 0.1.1 and later, on Windows): the share's sound is captured
 * through `getDisplayMedia` with echo cancellation on, whose reference is the
 * call audio this app is playing. See `installDesktopScreenShare`.
 */
export function hasEchoCancelledSystemAudio(): boolean {
  return desktopBridge()?.systemAudioCapture === 'echo-cancelled';
}

/** `true` only on the desktop client running on macOS. */
export function isDesktopMac(): boolean {
  return desktopBridge()?.platform === 'darwin';
}

/**
 * Runs `action` with the bridge when it exists, swallowing rejections.
 * A failed IPC call must never surface as an unhandled rejection or a toast —
 * the desktop layer is an enhancement, not a feature the user asked for.
 */
export function withDesktop(action: (bridge: DesktopBridge) => Promise<unknown> | void): void {
  const bridge = desktopBridge();
  if (bridge === null) return;
  try {
    const result = action(bridge);
    if (result instanceof Promise) void result.catch(() => undefined);
  } catch {
    /* The shell went away mid-call (reload, quit). Nothing to do. */
  }
}
